import { Clock, AlertCircle } from 'lucide-react';
import { ChatChannel } from '@/api/chat';
import { cn } from '@/lib/utils';
import { useFeatures, Feature } from '@/contexts/FeaturesContext';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

interface PendingSessionChannelItemProps {
  channel: ChatChannel;
  isSelected: boolean;
  onClick: () => void;
}

export function PendingSessionChannelItem({ channel, isSelected, onClick }: PendingSessionChannelItemProps) {
  // Check if any pending session needs user action
  const needsAction = channel.pendingSessions?.some(s => s.waitingForCurrentUser) ?? false;
  const pendingCount = channel.pendingSessions?.length ?? 0;
  
  // Get the most urgent pending description (first one that needs action, or just first)
  const urgentSession = channel.pendingSessions?.find(s => s.waitingForCurrentUser) 
    ?? channel.pendingSessions?.[0];
  
  const { isEnabled } = useFeatures();
  const showProfilePictures = isEnabled(Feature.PROFILE_PICTURE.name);

  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full p-2.5 hover:bg-accent rounded-lg transition-colors text-left',
        isSelected && 'bg-accent'
      )}
    >
      <div className="flex items-center gap-2.5">
        {/* Icon with action indicator */}
        <div className="relative flex-shrink-0">
          {channel.type === 'PRIVATE' && showProfilePictures && channel.directMessageAvatarUrl ? (
            <Avatar className="h-6 w-6">
              <AvatarImage src={`/api/v1/files/${channel.directMessageAvatarUrl}`} alt={channel.name || 'Direct message'} />
              <AvatarFallback>
                {(channel.name || 'D').split(' ').map(part => part[0]).join('').slice(0, 2).toUpperCase()}
              </AvatarFallback>
            </Avatar>
          ) : (
            <Clock className="h-4 w-4 text-amber-600" />
          )}
          {needsAction && (
            <div className="absolute -top-0.5 -right-0.5 h-2 w-2 bg-amber-500 rounded-full" />
          )}
        </div>
        
        <div className="flex-1 min-w-0">
          {/* Name and count */}
          <div className="flex items-center gap-2">
            <span className="font-medium truncate text-foreground">
              {channel.name || 'Chat'}
            </span>
            {pendingCount > 1 && (
              <span className="text-xs text-amber-600 font-medium">
                ({pendingCount})
              </span>
            )}
          </div>
          
          {/* Single line description */}
          {urgentSession && (
            <div className="flex items-center gap-1 text-xs text-muted-foreground truncate">
              {needsAction && <AlertCircle className="h-3 w-3 text-amber-600 flex-shrink-0" />}
              <span className={cn(needsAction && "text-amber-600")}>
                {urgentSession.pendingDescription}
              </span>
            </div>
          )}
        </div>
        
        {/* Unread indicator */}
        {channel.hasUnread && channel.unreadCount && channel.unreadCount > 0 && (
          <div className="h-2 w-2 rounded-full bg-amber-600 flex-shrink-0" />
        )}
      </div>
    </button>
  );
}
